import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { task } from 'ember-concurrency';
import { serializeQueryParams } from 'ember-fetch/utils/serialize-query-params';
import { t } from 'ember-intl';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

import type FetchService from 'umg-ember-backend/services/fetch';
import type FlashMessageService from 'umg-ember-backend/services/flash-messages';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

interface DirectorySignature {
  Args: {
    isOpen?: boolean;
    server: string;
    directory: string;
    selected?: string;
    showFullPath?: boolean;
    onSelect: (directory: string) => void;
  };
  Element: HTMLLIElement;
}

export class Directory extends Component<DirectorySignature> {
  // Services
  @service declare fetch: FetchService;
  @service declare flashMessages: FlashMessageService;

  // Defaults
  @tracked isOpen = false;
  @tracked subdirectories: string[] = [];

  // Getter and setter
  get isSelected() {
    return this.args.selected === this.args.directory;
  }

  get directoryName() {
    if (this.args.showFullPath) {
      return this.args.directory;
    }

    return this.args.directory?.split('/').pop();
  }

  // Hooks
  constructor(owner: Owner, args: DirectorySignature['Args']) {
    super(owner, args);

    if (this.args.isOpen) {
      this.isOpen = true;

      void this.queryDirectories.perform();
    }
  }

  // Actions
  toggleDirectory = async () => {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      await this.queryDirectories.perform();
    }
  };

  // Tasks
  queryDirectories = task({ restartable: true }, async () => {
    try {
      const result = (await this.fetch.post('/listDirectories', {
        processData: false,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        data: serializeQueryParams({
          server: this.args.server,
          node: this.args.directory,
        }),
      })) as string[];

      this.subdirectories = result.sort((a: string, b: string) => {
        return a.localeCompare(b);
      });
    } catch (e) {
      this.flashMessages.serverError(e);
    }
  });

  // Template
  <template>
    <li
      class={{bem (hash is-open=this.isOpen is-selected=this.isSelected)}}
      ...attributes
    >
      <div class={{bem 'label'}}>
        <div
          class={{bem 'icon'}}
          role='button'
          {{on 'click' this.toggleDirectory}}
        >
          {{svgJar
            (if this.isOpen 'chevron-down' 'chevron-right')
            class='u-icon'
          }}
        </div>
        <div
          class={{bem 'name'}}
          role='button'
          {{on 'click' (fn @onSelect @directory)}}
          {{on 'dblclick' this.toggleDirectory}}
        >
          {{this.directoryName}}
        </div>
      </div>

      {{#if this.isOpen}}
        <ul class={{bem 'subdirectories'}}>
          {{#if this.queryDirectories.isRunning}}
            {{t 'directory_select.directory.loading'}}
          {{else}}
            {{#each this.subdirectories as |subdirectory|}}
              <Directory
                @server={{@server}}
                @directory={{subdirectory}}
                @onSelect={{@onSelect}}
                @selected={{@selected}}
              />
            {{/each}}
          {{/if}}
        </ul>
      {{/if}}
    </li>
  </template>
}
